import PropTypes from "prop-types";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Box,
  Button,
  Card,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  IconButton,
  TextField,
  Typography,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import CloseIcon from "@mui/icons-material/Close";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import SearchIcon from "@mui/icons-material/Search";
import toast from "react-hot-toast";
import { customerApi } from "../../../api/customerApi";
import { getTone, toneAlpha, grey, darkVeil } from '../../../theme/tones';

const RELATION_TYPES = [
  "Famille",
  "Ami(e)",
  "Cabinet collaborateur",
  "Établissement",
  "Syndicat",
  "Associé",
  "Autre",
];

const ORANGE = getTone('orange').base;

const fullName = (p) =>
  `${p?.firstname || ""} ${p?.lastname || ""}`.trim() || "Apprenant";

const initials = (p) => {
  const f = (p?.firstname || "").trim();
  const l = (p?.lastname || "").trim();
  const letters = `${f.charAt(0)}${l.charAt(0)}` || f.slice(0, 2);
  return (letters || "?").toUpperCase();
};

// ---------------------------------------------------------------------------
// Graphe radial : l'apprenant courant au centre, ses relations autour.
// ---------------------------------------------------------------------------
const NetworkGraph = ({ apprenant, relations, onNodeClick }) => {
  const W = 900;
  const H = 560;
  const cx = W / 2;
  const cy = H / 2;
  const radius = relations.length === 1 ? 150 : 185;
  const centerR = 42;
  const nodeR = 34;

  const nodes = relations.map((rel, i) => {
    const angle = (-90 + (360 / relations.length) * i) * (Math.PI / 180);
    return {
      ...rel,
      x: cx + radius * Math.cos(angle),
      y: cy + radius * Math.sin(angle),
    };
  });

  return (
    <Box sx={{ width: "100%", overflow: "hidden" }}>
      <svg
        viewBox={`0 0 ${W} ${H}`}
        width="100%"
        height={H}
        style={{ display: "block", maxHeight: H }}
      >
        {/* Liens */}
        {nodes.map((n) => (
          <g key={`edge-${n.id}`}>
            <line
              x1={cx}
              y1={cy}
              x2={n.x}
              y2={n.y}
              stroke={getTone('indigo').light}
              strokeWidth={1.5}
            />
            <text
              x={(cx + n.x) / 2}
              y={(cy + n.y) / 2 - 5}
              textAnchor="middle"
              fontSize={11}
              fontWeight={700}
              fill={getTone('blue').color}
            >
              {n.relation_type}
            </text>
          </g>
        ))}

        {/* Noeuds reliés (cliquables -> fiche de l'apprenant) */}
        {nodes.map((n) => (
          <g
            key={`node-${n.id}`}
            onClick={() => onNodeClick && onNodeClick(n.customer_id)}
            style={{ cursor: "pointer" }}
          >
            <circle cx={n.x} cy={n.y} r={nodeR} fill={getTone('neutral').base} stroke={grey(0)} strokeWidth={3} />
            <text
              x={n.x}
              y={n.y}
              textAnchor="middle"
              dominantBaseline="central"
              fontSize={15}
              fontWeight={700}
              fill={grey(0)}
            >
              {initials(n)}
            </text>
            <text
              x={n.x}
              y={n.y + nodeR + 16}
              textAnchor="middle"
              fontSize={12}
              fontWeight={700}
              fill={grey(800)}
            >
              {fullName(n)}
            </text>
            {n.profession ? (
              <text
                x={n.x}
                y={n.y + nodeR + 30}
                textAnchor="middle"
                fontSize={10}
                fill={grey(400)}
              >
                {n.profession}
              </text>
            ) : null}
          </g>
        ))}

        {/* Noeud central (apprenant courant) */}
        <circle cx={cx} cy={cy} r={centerR} fill={grey(900)} stroke={ORANGE} strokeWidth={4} />
        <text
          x={cx}
          y={cy}
          textAnchor="middle"
          dominantBaseline="central"
          fontSize={18}
          fontWeight={800}
          fill={grey(0)}
        >
          {initials(apprenant)}
        </text>
        <text x={cx} y={cy + centerR + 18} textAnchor="middle" fontSize={13} fontWeight={800} fill={grey(900)}>
          {fullName(apprenant)}
        </text>
        {apprenant?.profession ? (
          <text x={cx} y={cy + centerR + 34} textAnchor="middle" fontSize={11} fill={grey(400)}>
            {apprenant.profession}
          </text>
        ) : null}
      </svg>
    </Box>
  );
};

NetworkGraph.propTypes = {
  // eslint-disable-next-line react/forbid-prop-types
  apprenant: PropTypes.object,
  // eslint-disable-next-line react/forbid-prop-types
  relations: PropTypes.array,
  onNodeClick: PropTypes.func,
};

// ---------------------------------------------------------------------------
// Dialog d'ajout de relation : type de lien + recherche d'un apprenant.
// ---------------------------------------------------------------------------
const AddRelationDialog = ({ open, onClose, customerId, excludeIds, onAdded }) => {
  const [type, setType] = useState("Cabinet collaborateur");
  const [query, setQuery] = useState("");
  const [candidates, setCandidates] = useState([]);
  const [selected, setSelected] = useState(null);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  // Reset à chaque ouverture
  useEffect(() => {
    if (open) {
      setType("Cabinet collaborateur");
      setQuery("");
      setSelected(null);
    }
  }, [open]);

  // Recherche (debounce)
  useEffect(() => {
    if (!open) return undefined;
    let active = true;
    setLoading(true);
    const handle = setTimeout(async () => {
      try {
        const data = await customerApi.getRelationCandidates(
          query,
          customerId,
          localStorage.getItem("accessToken")
        );
        if (active) {
          setCandidates(
            (data || []).filter((c) => !excludeIds.includes(Number(c.id)))
          );
        }
      } catch (err) {
        console.error(err);
      } finally {
        if (active) setLoading(false);
      }
    }, 300);
    return () => {
      active = false;
      clearTimeout(handle);
    };
  }, [open, query, customerId, excludeIds]);

  const handleAdd = async () => {
    if (!selected) return;
    setSaving(true);
    try {
      const res = await customerApi.addCustomerRelation(
        customerId,
        selected.id,
        type,
        localStorage.getItem("accessToken")
      );
      if (res.status === 200) {
        toast.success("Relation ajoutée !");
        onAdded();
        onClose();
      } else {
        toast.error(res.message || "Impossible d'ajouter la relation.");
      }
    } catch (err) {
      console.error(err);
      toast.error("Erreur lors de l'ajout de la relation.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{ sx: { borderRadius: "20px" } }}
    >
      <DialogTitle sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", fontWeight: 800 }}>
        Ajouter une relation
        <IconButton onClick={onClose} size="small">
          <CloseIcon fontSize="small" />
        </IconButton>
      </DialogTitle>
      <DialogContent>
        <Typography sx={{ fontWeight: 700, fontSize: "0.85rem", color: "text.secondary", mb: 1 }}>
          Type de lien
        </Typography>
        <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1, mb: 3 }}>
          {RELATION_TYPES.map((t) => {
            const active = t === type;
            return (
              <Chip
                key={t}
                label={t}
                onClick={() => setType(t)}
                variant="outlined"
                sx={{
                  fontWeight: 700,
                  borderRadius: "10px",
                  cursor: "pointer",
                  color: active ? ORANGE : getTone('neutral').color,
                  borderColor: active ? ORANGE : grey(200),
                  bgcolor: active ? toneAlpha('orange', 0.08) : "transparent",
                  "&:hover": { bgcolor: active ? toneAlpha('orange', 0.12) : grey(50) },
                }}
              />
            );
          })}
        </Box>

        <Typography sx={{ fontWeight: 700, fontSize: "0.85rem", color: "text.secondary", mb: 1 }}>
          Personne liée
        </Typography>
        <TextField
          fullWidth
          size="small"
          placeholder="Rechercher un apprenant..."
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          InputProps={{
            startAdornment: <SearchIcon sx={{ color: "text.disabled", mr: 1 }} />,
          }}
          sx={{ mb: 1.5, "& .MuiOutlinedInput-root": { borderRadius: "12px" } }}
        />

        <Box sx={{ maxHeight: 260, overflowY: "auto" }}>
          {loading ? (
            <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
              <CircularProgress size={26} />
            </Box>
          ) : candidates.length === 0 ? (
            <Typography sx={{ color: "text.disabled", textAlign: "center", py: 3 }}>
              Aucun apprenant trouvé.
            </Typography>
          ) : (
            candidates.map((c) => {
              const isSel = selected?.id === c.id;
              return (
                <Box
                  key={c.id}
                  onClick={() => setSelected(c)}
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 1,
                    px: 1.5,
                    py: 1.25,
                    borderRadius: "10px",
                    cursor: "pointer",
                    border: isSel ? `1.5px solid ${ORANGE}` : "1.5px solid transparent",
                    bgcolor: isSel ? toneAlpha('orange', 0.06) : "transparent",
                    "&:hover": { bgcolor: isSel ? toneAlpha('orange', 0.08) : grey(50) },
                  }}
                >
                  <Typography sx={{ fontWeight: 700, color: "text.primary" }}>
                    {[c.civility, fullName(c)].filter(Boolean).join(" ")}
                  </Typography>
                  {c.profession ? (
                    <Typography sx={{ color: "text.disabled", fontSize: "0.85rem" }}>
                      {c.profession}
                    </Typography>
                  ) : null}
                </Box>
              );
            })
          )}
        </Box>
      </DialogContent>
      <DialogActions sx={{ p: 2.5 }}>
        <Button onClick={onClose} color="inherit" sx={{ fontWeight: 700, borderRadius: "10px" }}>
          Annuler
        </Button>
        <Button
          onClick={handleAdd}
          disabled={!selected || saving}
          variant="contained"
          sx={{
            fontWeight: 800,
            borderRadius: "10px",
            px: 3,
            bgcolor: ORANGE,
            "&:hover": { bgcolor: getTone('orange').color },
          }}
        >
          {saving ? "Ajout..." : "Ajouter la relation"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

AddRelationDialog.propTypes = {
  open: PropTypes.bool,
  onClose: PropTypes.func,
  customerId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  excludeIds: PropTypes.array,
  onAdded: PropTypes.func,
};

// ---------------------------------------------------------------------------
// Onglet "Réseau pro"
// ---------------------------------------------------------------------------
const CustomerNetwork = ({ customerId, apprenant, relations = [], reload }) => {
  const [openDialog, setOpenDialog] = useState(false);
  const navigate = useNavigate();

  const goToRelation = useCallback(
    (relatedCustomerId) => {
      if (relatedCustomerId) {
        navigate(`/dashboard/customers/${relatedCustomerId}`);
      }
    },
    [navigate]
  );

  const excludeIds = useMemo(
    () => [Number(customerId), ...relations.map((r) => Number(r.customer_id))],
    [customerId, relations]
  );

  const handleDelete = useCallback(
    async (relationId) => {
      try {
        await customerApi.deleteCustomerRelation(
          relationId,
          customerId,
          localStorage.getItem("accessToken")
        );
        toast.success("Relation supprimée.");
        reload();
      } catch (err) {
        console.error(err);
        toast.error("Erreur lors de la suppression.");
      }
    },
    [customerId, reload]
  );

  return (
    <Grid container spacing={3}>
      {/* Zone graphe */}
      <Grid item xs={12} md={8}>
        <Card
          sx={{
            borderRadius: "16px",
            border: `1px solid ${grey(200)}`,
            boxShadow: `0 4px 20px -2px ${darkVeil(0.04)}`,
            minHeight: 560,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            p: 2,
          }}
        >
          {relations.length === 0 ? (
            <Box sx={{ textAlign: "center", px: 2 }}>
              <Typography sx={{ fontWeight: 800, color: "text.secondary", fontSize: "1.05rem" }}>
                Aucune relation enregistrée
              </Typography>
              <Typography sx={{ color: "text.disabled", mt: 0.5 }}>
                Ajoutez un lien pour construire le réseau.
              </Typography>
            </Box>
          ) : (
            <NetworkGraph
              apprenant={apprenant}
              relations={relations}
              onNodeClick={goToRelation}
            />
          )}
        </Card>
      </Grid>

      {/* Sidebar */}
      <Grid item xs={12} md={4}>
        <Button
          fullWidth
          startIcon={<AddIcon />}
          onClick={() => setOpenDialog(true)}
          sx={{
            mb: 2,
            py: 1.5,
            fontWeight: 800,
            textTransform: "none",
            color: ORANGE,
            border: `1.5px dashed ${ORANGE}`,
            borderRadius: "14px",
            bgcolor: toneAlpha('orange', 0.05),
            "&:hover": { bgcolor: toneAlpha('orange', 0.1), border: `1.5px dashed ${ORANGE}` },
          }}
        >
          Ajouter une relation
        </Button>

        {relations.length === 0 ? (
          <Card
            sx={{
              borderRadius: "14px",
              border: `1.5px dashed ${grey(200)}`,
              boxShadow: "none",
              p: 3,
              textAlign: "center",
            }}
          >
            <Typography sx={{ color: "text.disabled", fontSize: "0.9rem" }}>
              Aucune relation.
              <br />
              Ajoutez un lien professionnel ou familial.
            </Typography>
          </Card>
        ) : (
          <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
            {relations.map((rel) => (
              <Card
                key={rel.id}
                onClick={() => goToRelation(rel.customer_id)}
                sx={{
                  borderRadius: "14px",
                  border: `1px solid ${grey(200)}`,
                  boxShadow: `0 2px 8px -2px ${darkVeil(0.05)}`,
                  p: 2,
                  cursor: "pointer",
                  transition: "border-color 0.2s, box-shadow 0.2s",
                  "&:hover": {
                    borderColor: getTone('indigo').base,
                    boxShadow: `0 6px 16px -4px ${toneAlpha('indigo', 0.18)}`,
                  },
                }}
              >
                <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 1 }}>
                  <Typography
                    sx={{
                      fontWeight: 800,
                      color: "text.primary",
                      whiteSpace: "nowrap",
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      flex: 1,
                    }}
                  >
                    {fullName(rel)}
                  </Typography>
                  <Chip
                    label={rel.relation_type}
                    size="small"
                    sx={{
                      fontWeight: 700,
                      fontSize: "0.72rem",
                      color: "secondary.dark",
                      bgcolor: toneAlpha('blue', 0.12),
                      borderRadius: "8px",
                    }}
                  />
                  <IconButton
                    size="small"
                    onClick={(e) => {
                      e.stopPropagation();
                      handleDelete(rel.id);
                    }}
                    sx={{ color: grey(300), "&:hover": { color: "error.main" } }}
                  >
                    <DeleteOutlineIcon fontSize="small" />
                  </IconButton>
                </Box>
                {rel.profession ? (
                  <Typography sx={{ color: "text.disabled", fontSize: "0.82rem", mt: 0.5 }}>
                    {rel.profession}
                  </Typography>
                ) : null}
              </Card>
            ))}
          </Box>
        )}
      </Grid>

      <AddRelationDialog
        open={openDialog}
        onClose={() => setOpenDialog(false)}
        customerId={customerId}
        excludeIds={excludeIds}
        onAdded={reload}
      />
    </Grid>
  );
};

CustomerNetwork.propTypes = {
  customerId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  // eslint-disable-next-line react/forbid-prop-types
  apprenant: PropTypes.object,
  // eslint-disable-next-line react/forbid-prop-types
  relations: PropTypes.array,
  reload: PropTypes.func,
};

export default CustomerNetwork;
